import { usePermissions } from '@/context/PermissionsContext'
import { ReactNode } from 'react'

import { Permission, Role } from '@droidmap/permissions-service-contract'

export interface PermissionGateProps {
  children: ReactNode
  fallback?: ReactNode

  // Permission-based checks
  permission?: Permission
  permissions?: Permission[]
  requireAll?: boolean // If true, user must have ALL permissions. If false (default), user needs ANY

  // Role-based checks
  role?: Role
  resourceType?: 'organization' | 'project'
  resourceId?: string

  // Custom permission function
  customCheck?: () => boolean

  // Loading state handling
  showLoadingFallback?: boolean
}

/**
 * PermissionGate component for conditional rendering based on user permissions
 *
 * Usage examples:
 *
 * // Simple permission check
 * <PermissionGate permission={Permission.CREATE_PROJECT}>
 *   <Button>Create Project</Button>
 * </PermissionGate>
 *
 * // Multiple permissions (ANY)
 * <PermissionGate permissions={[Permission.VIEW_PROJECT, Permission.LIST_PROJECTS]}>
 *   <ProjectList />
 * </PermissionGate>
 *
 * // Multiple permissions (ALL required)
 * <PermissionGate permissions={[Permission.UPDATE_PROJECT, Permission.DELETE_PROJECT]} requireAll>
 *   <AdminPanel />
 * </PermissionGate>
 *
 * // Role-based check
 * <PermissionGate role={Role.ORG_ADMIN} resourceType="organization">
 *   <OrgSettings />
 * </PermissionGate>
 *
 * // Custom permission logic
 * <PermissionGate customCheck={() => hasSpecialAccess()}>
 *   <UserManagement />
 * </PermissionGate>
 *
 * // With fallback content
 * <PermissionGate
 *   permission={Permission.VIEW_PROJECT}
 *   fallback={<div>Access Denied</div>}
 * >
 *   <ProjectDetails />
 * </PermissionGate>
 */
export const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  fallback = null,
  permission,
  permissions,
  requireAll = false,
  role,
  resourceType,
  resourceId,
  customCheck,
  showLoadingFallback = false,
}) => {
  const { hasPermission, hasAnyPermission, hasRole, isLoading } =
    usePermissions()

  // Show loading state if requested
  if (isLoading && showLoadingFallback) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{fallback}</>
  }

  let hasAccess = false

  // Custom check takes precedence
  if (customCheck) {
    hasAccess = customCheck()
  }
  // Single permission check
  else if (permission) {
    hasAccess = hasPermission(permission, resourceId)
  }
  // Multiple permissions check
  else if (permissions && permissions.length > 0) {
    if (requireAll) {
      hasAccess = permissions.every((perm) => hasPermission(perm, resourceId))
    } else {
      hasAccess = hasAnyPermission(permissions, resourceId)
    }
  }
  // Role check
  else if (role && resourceType) {
    hasAccess = hasRole(role, resourceType, resourceId)
  }
  // If no checks are specified, default to allowing access
  else {
    hasAccess = true
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return hasAccess ? <>{children}</> : <>{fallback}</>
}

/**
 * Higher-order component version for complex permission wrapping
 */
export const withPermissions = <P extends object>(
  Component: React.ComponentType<P>,
  permissionProps: Omit<PermissionGateProps, 'children'>,
) => {
  return (props: P) => (
    <PermissionGate {...permissionProps}>
      <Component {...props} />
    </PermissionGate>
  )
}

export default PermissionGate
